import { logger } from '@adornis/base/logging.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { prepareMessage } from '@adornis/mails/server/communication.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { getUserByID } from '../api/users.js';
import { getInstitutionByID } from '../api/institution.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { mailer } from '../mails/communication.js';
import { buildNewsletterOptinLink } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { getNotificationType } from './_helper.js';

// Mail template for Newsletter Opt-In

defineMailTemplate(MailTemplateType.NEWSLETTER_OPTIN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Newsletter Anmeldung bestätigen',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>vielen Dank für Ihr Interesse an unserem Newsletter!</p>
<p>Um die Anmeldung abzuschließen und unseren Newsletter zu erhalten, klicken Sie bitte auf den folgenden Link:</p>
<p>{{newsletter-optin-link}}</p>
<p>Falls Sie diese E-Mail versehentlich erhalten haben oder sich nicht für unseren Newsletter anmelden möchten, können Sie diese E-Mail einfach ignorieren.</p>
<p>Viele Grüße</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>Thank you for your interest in our newsletter!</p>
<p>To complete the registration and receive our newsletter, please click on the following link:</p>
<p>{{newsletter-optin-link-en}}</p>
<p>If you received this email by mistake or do not wish to subscribe to our newsletter, you can simply ignore this email.</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.NEWSLETTER_OPTIN),
  attachments: [],
  displayName: 'Newsletter Opt-In',
  editorHint: () => institutionMailHint(MailTemplateType.NEWSLETTER_OPTIN),
});

export async function notifyNewsletterOptIn(userID: string, institutionID?: string) {
  // Get the user data
  const user = await getUserByID(userID)({
    _id: 1,
    title: 1,
    firstName: 1,
    lastName: 1,
    email: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
    emailConfirmed: 1,
  });

  if (!user) {
    logger.error({ userID }, 'User not found when trying to send newsletter opt-in email');
    throw new Error(`User not found: ${userID}`);
  }

  // Get institution with color definitions if institutionID is provided
  if (institutionID) {
    const institution = await getInstitutionByID(institutionID)({
      _id: 1,
      name: 1,
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      settings: {
        colorDefinitions: selectionSet(() => ColorDefinitions, 4),
        inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
      },
    });

    // Set mail theme
    if (institution) {
      bhBaseQlContext.colorDefinitions =
        institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;
    }
  }

  // Get the newsletter opt-in mail template
  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.NEWSLETTER_OPTIN,
  )(InstitutionMailTemplate.allFields);

  if (!mailTemplate) {
    logger.error({ institutionID }, 'Newsletter opt-in template not found');
    throw new Error('Newsletter opt-in email template not found');
  }

  // Create login token for newsletter opt-in link
  const token = await generateLoginToken({
    userID,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  // Prepare email variables
  const mailVars = {
    [MailTemplateVars.TITLE]: user.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
    [MailTemplateVars.NEWSLETTER_OPTIN_LINK]: buildNewsletterOptinLink(token, 'de'),
    [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN]: buildNewsletterOptinLink(token, 'en'),
  };

  const { subject, content } = await assembleInstitutionMail({ template: mailTemplate, infos: mailVars as any });

  if (!content) throw new Error('mail template has no content!');
  if (!subject) throw new Error('mail template has no subject!');

  // Determine notification type and send
  const notificationType = await getNotificationType(user, mailTemplate, institutionID);

  if (notificationType === 'email') {
    const recipient = new Recipient(user.email);
    const message = await prepareMessage({
      subject,
      html: content,
      attachments: [],
    });

    try {
      const _mailer = await mailer;
      if (!_mailer) throw new Error('Could not get mailer');
      await _mailer.sendMail(message, recipient);
      logger.info({ email: user.email }, 'Newsletter opt-in email sent successfully');
      return true;
    } catch (err) {
      logger.warn({ email: user.email, err }, 'Failed to send newsletter opt-in email');
      return false;
    }
  } else {
    // No notification sent
    logger.warn(
      { notificationType, userID },
      'No newsletter opt-in notification sent because email notification is not activated',
    );
    return false;
  }
}
